import { useEffect, useRef, useState } from 'react'

import { Helmet } from 'react-helmet'
import NavBar from 'components/navbar/NavBar'
import Header from 'components/typography/Header'
import Plan from 'components/pricing/Plan'
import styles from './styles/Pricing.css'
import Link from 'components/misc/Link'
import * as analytics from 'lib/analytics'
import Paragraph from '../components/typography/Paragraph'
import Button from '../components/forms/Button'
import { useDispatch, useSelector } from 'react-redux'
import { toggleModal } from '../store/ui/actions'
import Endorsements from 'components/pricing/Endorsements'
import TabbedView from '../components/misc/TabbedView'
import { LeadGenForm } from '../components/pricing/LeadGenForm'
import FeatureTable from '../components/pricing/FeatureTable'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import RedeemCode from '../components/pricing/RedeemCode'
import { errorToast } from '../lib/toast'
import loginModalStore from 'store/login-modal'
import injectAsyncStore from 'lib/inject-async-store'

const DESCRIPTION_COPY = `
Fundamentally change the way you do research.
Search and discover millions of citations showing expert insights and analyses on any topic and any publication starting at $7.99(USD)/month.
`

const IndividualPlans = ({ setTab }) => (
  <>
    <div className={styles.specialOffer}>
      <div>
        <Header level={4} center>What does scite offer?</Header>
        <Paragraph className={styles.offerParagraph} center>
          Thousands of students and researchers trust scite to help them find citations while writing, discover well supported and replicated evidence for their claims, and find critical debate, disagreement, and gaps in understanding.
          <br />
          <Link blue underline to='/endorsements'>See what students are saying about scite.</Link>
        </Paragraph>
      </div>
      <FeatureTable />
      <img alt='recommend institution' width='76px' height='76px' src={`${CONFIG.assetsBaseURL}/images/icons/institution.svg`} />
      <Header className={styles.specialOfferHeader} level={3}>Get scite for a year!</Header>
      <div className={styles.specialOfferSubheading}>
        <strong>Recommend a trial to your organization and we will give you an individual upgrade to premium+ for a year.</strong> Email your department head or librarian to request a no obligation organization-wide trial and cc <Link blue href='mailto:sales@scite.ai'>sales@scite.ai</Link> and then let us know and we will upgrade your account.
      </div>
      <div className={styles.specialOfferSubheading}>
        <Header className={styles.faqHeader} level={4} center>Help & FAQ</Header>
        <Paragraph className={styles.faqParagraph} type='small' light center>
          If you have any problems with your subscription feel free to contact us at <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link> anytime and we will get right back to you.
          All prices in USD. Want us to issue an invoice? <Link blue underline href='mailto:sales@scite.ai'>contact us</Link>
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>Do you offer group plans or discounts?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We do! If you are interested in a group plan, please contact us at <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link> for a quote. Or <Link blue underline onClick={() => setTab('organizations')}>see our group plans for more details.</Link>
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>These prices are too expensive for me!</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We are a small team working hard to make scite the best research discovery and evaluation tool available. We rely on our user's support to continue running scite.
          We do offer discounts for students and academics which you can redeem above by clicking "Reedem Discount".
          If the prices are still too expensive for you, we can still help you out. Contact us at <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link> and let us know what you think is fair and we will work with you to find a fair price.
          Additionally, if you recommend a scite license to your organization, we will give you an individual upgrade to premium+ for a year.
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>Don't see a payment method you want to use?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We can arrange alternative methods of payment such as Bank Transfer, Paypal, Alipay, WeChat, etc. Please contact us at <Link blue underline href='mailto:sales@scite.ai'>sales@scite.ai</Link> and let us know which plan you'd like to purchase.
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>I am a researcher who wants to use scite data for research, do I have to pay?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          It depends on the project! For most projects, you can use scite data for free without needed a paid plan, just let us know what your project is at <Link blue underline href='mailto:hi@scite.ai'>hi@scite.ai</Link>.
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>How do your payments work?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          We use stripe to manage our payments, we do not store or manage credit card information.
        </Paragraph>
        <Header level={5} className={styles.faqHeader}>I purchased a plan elsewhere and received a code, how can I redeem it?</Header>
        <Paragraph className={styles.faqParagraph} type='small' light>
          If you have a redemption code, enter it here to redeem your plan. Please note this is a one-time redemption code purchased elsewhere and <i>not</i> a promotion or discount code.
        </Paragraph>
        <RedeemCode />
      </div>
    </div>
    <Endorsements className={styles.endorsements} />
  </>
)

const OrganizationPlans = () => {
  const scrollRef = useRef(null)
  const handleSeeMore = () => scrollRef?.current?.scrollIntoView()
  return (
    <>
      <div className={styles.plansLayout}>
        <Plan plan='group' handleSeeMore={handleSeeMore} className={styles.orgPlan} />
        <Plan plan='organization' handleSeeMore={handleSeeMore} className={styles.orgPlan} />
        <Plan plan='integrate' handleSeeMore={handleSeeMore} className={styles.orgPlan} />
        <Plan plan='data' handleSeeMore={handleSeeMore} className={styles.orgPlan} />
      </div>
      <div className={styles.specialOffer}>
        <img alt='recommend institution' width='76px' height='76px' src={`${CONFIG.assetsBaseURL}/images/icons/institution.svg`} />
        <Header className={styles.specialOfferHeader} level={3}>Start an organization-wide trial</Header>
        <div className={styles.specialOfferSubheading}>
          We offer a no obligation free organization-wide trial which will provide scite premium to all your users. <Link blue href={CONFIG.trialFormURL}>Click here</Link> to set up authentication and get started with a trial.
        </div>
        <div className={styles.specialOffer}>
          <Header className={styles.specialOfferHeader} level={3}>Request a quote or a demo</Header>
          <div className={styles.specialOfferSubheading}>
            scite pricing is based on the number of students, faculty, and staff at your institution. We offer organization-wide annual licenses with volume-based discounts. Contact us at <Link blue href='mailto:sales@scite.ai'>sales@scite.ai</Link> to request a quote or get a demo.
          </div>
        </div>
        <div ref={scrollRef} className={styles.specialOffer}>
          <Header className={styles.specialOfferHeader} level={3}>Want to know more?</Header>
          <div>
            <ul>
              <li>See how we help <Link blue underline to='/partners/academic-institutions'>Universities & Academic Institutions</Link></li>
              <li>See how we help <Link blue underline to='/partners/pharmaceuticals-and-biotech'>Pharma & Biotech</Link></li>
              <li>See how we help <Link blue underline to='/parters/publishers'>Publishers</Link></li>
              <li><Link blue underline to='/apiterms'>Get started with integration</Link></li>
            </ul>
            Checkout <Link blue underline to='/partners'>how we help our partners</Link> or <Link blue underline to='/coverage-and-comparison'>see our coverage</Link> and <Link blue underline to='/coverage-and-comparison'>how we compare with other indexes</Link>
          </div>
        </div>
        <LeadGenForm />
      </div>
      <Endorsements
        className={styles.endorsements}
        subheader='Join leading organizations like University of Hong Kong and Cold Spring Harbor Laboratories in providing their users with the power to understand what is said about publications and their findings by other researchers'
      />
    </>
  )
}

export const Pricing = () => {
  const dispatch = useDispatch()
  const location = useLocation()

  const user = useSelector(({ ui }) => ui.user)
  const [tab, setTab] = useState('individuals')
  const [error, setError] = useState()

  useEffect(() => {
    const query = qs.parse(location.search)
    if (query.tab) {
      setTab(query.tab)
    }
    if (query.error) {
      let errorCopy = 'Something went wrong. Please try again or contact us at sales@scite.ai'
      if (query.error === 'no-user') {
        errorCopy = 'You must be signed in to purchase a plan. If this continues to happen please contact us at sales@scite.ai'
      } else if (query.error === 'select-price') {
        errorCopy = 'Please select a price to purchase for. If this continues to happen please contact us at sales@scite.ai'
      }
      setError(errorCopy)
    }
  }, [])

  useEffect(() => {
    analytics.event({
      category: 'Pricing',
      action: 'View Pricing Page',
      label: tab
    })
    window.scrollTo(0, 0)
  }, [tab])

  useEffect(() => {
    if (error) {
      errorToast({
        message: error,
        durationMs: 5500
      })
    }
  }, [error])

  return (
    <div className={styles.container}>
      <Helmet>
        <title>scite pricing</title>

        <meta property='title' content='scite pricing' />
        <meta property='og:title' content='scite pricing' />
        <meta property='twitter:title' content='scite pricing' />

        <meta name='description' content={DESCRIPTION_COPY} />
        <meta property='og:description' content={DESCRIPTION_COPY} />
        <meta property='twitter:description' content={DESCRIPTION_COPY} />

        <meta property='og:image' content='http://image.thum.io/get/ogImage/https://scite.ai/pricing?bannerClosed=true' />
        <meta property='twitter:image' content='http://image.thum.io/get/ogImage/https://scite.ai/pricing?bannerClosed=true' />

      </Helmet>
      <NavBar staticPage homePage />
      <div className={styles.pricingPageLayout}>
        <Header level={3} className={styles.header} center>
          Fundamentally change the way you do research
        </Header>
        <Paragraph center className={styles.pricingSubheading}>
          Search and discover millions of expert insights and analyses on any topic and any publication
        </Paragraph>
        {!user && (<Button color='blue' bold onClick={() => dispatch(toggleModal('login'))}>Activate two week trial</Button>)}
        <TabbedView
          className={styles.tabbedView}
          viewClassName={styles.tabbedViewView}
          tabBarClassName={styles.tabBarView}
          tabClassName={styles.tab}
          tabSelectedClassName={styles.tabSelected}
          selected={tab}
          onSelected={(id, name) => setTab(name)}
          tabs={[
            {
              id: 'individuals',
              label: 'For Individuals',
              view: () => <IndividualPlans setTab={setTab} />
            },
            {
              id: 'organizations',
              label: 'For Organizations and Groups',
              view: () => <OrganizationPlans />
            }
          ]}
        />
      </div>
    </div>
  )
}

export default injectAsyncStore({
  loginModal: loginModalStore
})(Pricing)
